// src/pages/Storage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";

// Domyślny katalog lokalizacji (możesz dopisać/zmienić pod siebie)
const DEFAULT_LOCATIONS = [
  "Harcerska",
  "Północna",
  "Mszana",
  "Katowicka",
];

export default function Storage() {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  // Dodawanie nowej pozycji
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newImg, setNewImg] = useState(null);
  const [newLocs, setNewLocs] = useState(() =>
    DEFAULT_LOCATIONS.reduce((acc, k) => ({ ...acc, [k]: 0 }), {})
  );
  const [customLocInput, setCustomLocInput] = useState("");

  // Edycja stanów wybranej pozycji
  const selectedItem = useMemo(
    () => items.find((it) => it.id === selectedId) || null,
    [items, selectedId]
  );
  const [editLocs, setEditLocs] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [adding, setAdding] = useState(false);

  // Subskrypcja kolekcji
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "inventory"), (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems(
        list.sort((a, b) =>
          (a?.name || "").toLowerCase().localeCompare((b?.name || "").toLowerCase())
        )
      );
    });
    return () => unsub();
  }, []);

  // Gdy wybierzesz pozycję, wczytaj jej stany do edycji
  useEffect(() => {
    if (!selectedItem) {
      setEditLocs({});
      return;
    }
    const base = { ...(selectedItem.locations || {}) };
    // Upewnij się, że mamy wszystkie znane lokalizacje w obiekcie
    DEFAULT_LOCATIONS.forEach((k) => {
      if (typeof base[k] !== "number") base[k] = 0;
    });
    setEditLocs(base);
  }, [selectedItem]);

  const totalFrom = (obj) =>
    Object.values(obj || {}).reduce((sum, v) => sum + (Number(v) || 0), 0);

  // Dodaj lokalizację w formularzu dodawania
  const addCustomLoc = () => {
    const key = (customLocInput || "").trim();
    if (!key) return;
    if (newLocs[key] !== undefined) {
      setCustomLocInput("");
      return;
    }
    setNewLocs((p) => ({ ...p, [key]: 0 }));
    setCustomLocInput("");
  };

  // Upload obrazka do Storage i zwróć URL
  const uploadImageAndGetUrl = async (docId, file) => {
    if (!file) return "";
    const safeName = file.name.replace(/\s+/g, "_");
    const fileRef = ref(storage, `inventory/${docId}/${Date.now()}_${safeName}`);
    await uploadBytes(fileRef, file);
    return await getDownloadURL(fileRef);
  };

  // Dodaj nowy item
  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      // 1) utwórz dokument (bez imageUrl)
      const baseDoc = {
        name: newName.trim(),
        description: newDesc.trim(),
        locations: Object.entries(newLocs).reduce((acc, [k, v]) => {
          acc[k] = Number(v) || 0;
          return acc;
        }, {}),
        imageUrl: "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const refDoc = await addDoc(collection(db, "inventory"), baseDoc);

      // 2) upload obrazka i update
      let imageUrl = "";
      if (newImg) {
        imageUrl = await uploadImageAndGetUrl(refDoc.id, newImg);
        await updateDoc(doc(db, "inventory", refDoc.id), {
          imageUrl,
          updatedAt: serverTimestamp(),
        });
      }

      // 3) reset formu
      setIsAddOpen(false);
      setNewName("");
      setNewDesc("");
      setNewImg(null);
      setNewLocs(DEFAULT_LOCATIONS.reduce((acc, k) => ({ ...acc, [k]: 0 }), {}));
    } catch (e) {
      console.error("Add item error:", e);
      alert("Błąd dodawania pozycji.");
    } finally {
      setAdding(false);
    }
  };

  // Zapis edycji stanów magazynowych
  const handleSaveEdit = async () => {
    if (!selectedItem) return;
    setSavingEdit(true);
    try {
      const nextLocs = Object.entries(editLocs || {}).reduce((acc, [k, v]) => {
        acc[k] = Number(v) || 0;
        return acc;
      }, {});
      await updateDoc(doc(db, "inventory", selectedItem.id), {
        locations: nextLocs,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("Save edit error:", e);
      alert("Błąd zapisu.");
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "320px 1fr",
        gap: 16,
        minHeight: "calc(100vh - 40px)",
        padding: 20,
      }}
    >
      {/* LEWA KOLUMNA – lista */}
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: 12,
            borderBottom: "1px solid #e5e7eb",
            justifyContent: "space-between",
          }}
        >
          <strong>Przedmioty</strong>
          <button
            onClick={() => setIsAddOpen((v) => !v)}
            style={{
              fontSize: 12,
              padding: "6px 10px",
              border: "1px solid #2563eb",
              color: "#2563eb",
              borderRadius: 6,
              background: "transparent",
              cursor: "pointer",
            }}
          >
            + Dodaj
          </button>
        </div>

        {/* Lista */}
        <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {items.map((it) => (
            <div
              key={it.id}
              onClick={() => setSelectedId(it.id)}
              style={{
                display: "flex",
                gap: 10,
                padding: 10,
                borderBottom: "1px solid #f1f5f9",
                background: selectedId === it.id ? "#eef2ff" : "white",
                cursor: "pointer",
              }}
            >
              <img
                src={it.imageUrl || "https://via.placeholder.com/48x48?text=IMG"}
                alt=""
                style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }}
              />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{it.name}</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  Razem: {totalFrom(it.locations || {})}
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: "#6b7280" }}>Brak pozycji.</div>
          )}
        </div>

        {/* Formularz dodawania */}
        {isAddOpen && (
          <div style={{ padding: 12, borderTop: "1px solid #e5e7eb", background: "#fafafa" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <input
                placeholder="Nazwa"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                style={{
                  padding: 8,
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  fontSize: 13,
                  outline: "none",
                }}
              />
              <textarea
                placeholder="Opis"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                rows={3}
                style={{
                  padding: 8,
                  border: "1px solid #d1d5db",
                  borderRadius: 6,
                  fontSize: 13,
                  outline: "none",
                  resize: "vertical",
                }}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>Stany (per lokalizacja)</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 80px", gap: 6 }}>
                  {Object.keys(newLocs).map((loc) => (
                    <React.Fragment key={loc}>
                      <div style={{ fontSize: 12, color: "#374151", alignSelf: "center" }}>
                        {loc}
                      </div>
                      <input
                        type="number"
                        min={0}
                        value={newLocs[loc]}
                        onChange={(e) =>
                          setNewLocs((p) => ({ ...p, [loc]: Number(e.target.value || 0) }))
                        }
                        style={{
                          padding: 6,
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          fontSize: 12,
                          outline: "none",
                          textAlign: "right",
                        }}
                      />
                    </React.Fragment>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <input
                    placeholder="Dodaj własną lokalizację"
                    value={customLocInput}
                    onChange={(e) => setCustomLocInput(e.target.value)}
                    style={{
                      flex: 1,
                      padding: 8,
                      border: "1px solid #d1d5db",
                      borderRadius: 6,
                      fontSize: 12,
                      outline: "none",
                    }}
                  />
                  <button
                    onClick={addCustomLoc}
                    style={{
                      fontSize: 12,
                      padding: "6px 10px",
                      border: "1px solid #6b7280",
                      color: "#374151",
                      borderRadius: 6,
                      background: "transparent",
                      cursor: "pointer",
                    }}
                  >
                    Dodaj lok.
                  </button>
                </div>
              </div>

              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setNewImg(e.target.files?.[0] || null)}
                />
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button
                  disabled={adding}
                  onClick={handleAdd}
                  style={{
                    fontSize: 12,
                    padding: "8px 12px",
                    border: "1px solid #2563eb",
                    color: "white",
                    background: "#2563eb",
                    borderRadius: 6,
                    cursor: "pointer",
                    opacity: adding ? 0.7 : 1,
                  }}
                >
                  {adding ? "Dodaję…" : "Dodaj pozycję"}
                </button>
                <button
                  onClick={() => setIsAddOpen(false)}
                  style={{
                    fontSize: 12,
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    color: "#374151",
                    background: "white",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  Anuluj
                </button>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* PRAWA KOLUMNA – szczegóły */}
      <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 16 }}>
        {!selectedItem ? (
          <div style={{ color: "#6b7280", fontSize: 14 }}>Wybierz pozycję z listy.</div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 16 }}>
              <img
                src={selectedItem.imageUrl || "https://via.placeholder.com/200x140?text=IMG"}
                alt=""
                style={{
                  width: 220,
                  height: 160,
                  objectFit: "cover",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                }}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{selectedItem.name}</div>
                <div style={{ fontSize: 13, color: "#374151", whiteSpace: "pre-wrap" }}>
                  {selectedItem.description || "—"}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                  Razem sztuk: {totalFrom(editLocs)}
                </div>
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                Stany (edytuj i zapisz):
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 8 }}>
                {Object.keys(editLocs).map((loc) => (
                  <React.Fragment key={loc}>
                    <div
                      style={{
                        fontSize: 12,
                        color: "#374151",
                        alignSelf: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={loc}
                    >
                      {loc}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        onClick={() =>
                          setEditLocs((p) => ({ ...p, [loc]: Math.max(0, (Number(p[loc]) || 0) - 1) }))
                        }
                        style={{
                          width: 32,
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          background: "white",
                          cursor: "pointer",
                        }}
                      >
                        −
                      </button>
                      <input
                        type="number"
                        min={0}
                        value={editLocs[loc]}
                        onChange={(e) =>
                          setEditLocs((p) => ({ ...p, [loc]: Number(e.target.value || 0) }))
                        }
                        style={{
                          flex: 1,
                          padding: 6,
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          fontSize: 12,
                          textAlign: "right",
                          outline: "none",
                        }}
                      />
                      <button
                        onClick={() =>
                          setEditLocs((p) => ({ ...p, [loc]: (Number(p[loc]) || 0) + 1 }))
                        }
                        style={{
                          width: 32,
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          background: "white",
                          cursor: "pointer",
                        }}
                      >
                        +
                      </button>
                    </div>
                  </React.Fragment>
                ))}
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  disabled={savingEdit}
                  onClick={handleSaveEdit}
                  style={{
                    fontSize: 12,
                    padding: "8px 12px",
                    border: "1px solid #16a34a",
                    color: "white",
                    background: "#16a34a",
                    borderRadius: 6,
                    cursor: "pointer",
                    opacity: savingEdit ? 0.7 : 1,
                  }}
                >
                  {savingEdit ? "Zapisuję…" : "Zapisz zmiany"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
